import { useAdapter } from '../../application/data/use-adapter';
import type { TableAdapter } from '../../ports';
import { Icon } from '../shared/Icon';

const COUNT_FMT = new Intl.NumberFormat('en-GB');

function formatCount(value: number): string {
    return COUNT_FMT.format(value);
}

export interface PaginationProps<T> {
    adapter: TableAdapter<T>;
}

interface RangeLabelProps {
    offset: number;
    count: number;
    total?: number;
}

function RangeLabel(props: RangeLabelProps): JSX.Element | null {
    const { offset, count, total } = props;
    if (total === undefined || count === 0) return null;
    return (
        <span className="ics-table__page-range">
            {formatCount(offset + 1)} to{' '}
            <strong>{formatCount(offset + count)}</strong> of{' '}
            <strong>{formatCount(total)}</strong>
        </span>
    );
}

export function TablePagination<T>(
    props: PaginationProps<T>,
): JSX.Element | null {
    const { adapter } = props;
    const snapshot = useAdapter(adapter);
    const { page, hasNext, hasPrev, loading, pageCount, capabilities } =
        snapshot;

    if (!hasNext && page === 0) return null;

    const showFirst = adapter.first !== undefined;
    const showPrev = adapter.prev !== undefined;
    const showNext = adapter.next !== undefined;
    const showLast =
        adapter.last !== undefined && capabilities.totalCount === true;

    return (
        <nav className="ics-table__pagination" aria-label="Pagination">
            <RangeLabel
                offset={snapshot.offset}
                count={snapshot.rows.length}
                total={snapshot.totalCount}
            />
            <div className="ics-table__page-controls">
                {showFirst ? (
                    <button
                        type="button"
                        className="ics-table__page-button"
                        aria-label="First page"
                        onClick={() => adapter.first?.()}
                        disabled={loading || page === 0}
                    >
                        <Icon name="first_page" />
                    </button>
                ) : null}
                {showPrev ? (
                    <button
                        type="button"
                        className="ics-table__page-button"
                        aria-label="Previous page"
                        onClick={() => adapter.prev?.()}
                        disabled={loading || !hasPrev}
                    >
                        <Icon name="chevron_left" />
                    </button>
                ) : null}
                <span className="ics-table__page-label">
                    Page <strong>{formatCount(page + 1)}</strong>
                    {pageCount === undefined ? null : (
                        <>
                            {' '}
                            of <strong>{formatCount(pageCount)}</strong>
                        </>
                    )}
                </span>
                {showNext ? (
                    <button
                        type="button"
                        className="ics-table__page-button"
                        aria-label="Next page"
                        onClick={() => adapter.next?.()}
                        disabled={loading || !hasNext}
                    >
                        <Icon name="chevron_right" />
                    </button>
                ) : null}
                {showLast ? (
                    <button
                        type="button"
                        className="ics-table__page-button"
                        aria-label="Last page"
                        onClick={() => adapter.last?.()}
                        disabled={loading || !hasNext}
                    >
                        <Icon name="last_page" />
                    </button>
                ) : null}
            </div>
        </nav>
    );
}
